import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FolderKanban,
  LayoutList,
  LoaderCircle,
  LogOut,
  MessageSquareText,
  Plus,
  Save,
  Send,
  Sparkles,
  Users,
} from "lucide-react";
import {
  researchApi,
  type Conversation,
  type ConversationMessage,
  type JsonObject,
  type Panel,
  type PanelMember,
  type Project,
  type Survey,
  type SurveyQuestion,
  type TestRun,
} from "./api";
import { AuthProvider, useAuth } from "./auth";
import { LoginPage } from "./LoginPage";

type Section =
  | "projects"
  | "panels"
  | "surveys"
  | "message-test"
  | "conversation"
  | "survey-test";

type Action = (
  run: () => Promise<void>,
  success?: string,
  label?: string,
) => Promise<void>;

const nav: Array<{
  id: Section;
  label: string;
  icon: typeof FolderKanban;
}> = [
  { id: "projects", label: "프로젝트", icon: FolderKanban },
  { id: "panels", label: "AI 패널", icon: Users },
  { id: "surveys", label: "설문", icon: ClipboardList },
  { id: "message-test", label: "문구 테스트", icon: BarChart3 },
  { id: "conversation", label: "패널 대화", icon: MessageSquareText },
  { id: "survey-test", label: "설문 테스트", icon: LayoutList },
];

function Button({
  children,
  onClick,
  disabled,
  secondary = false,
  loading = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  secondary?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      className={secondary ? "button secondary" : "button"}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? <LoaderCircle className="spin" size={16} /> : null}
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="empty">{text}</div>;
}

function SelectScope({
  projects,
  panels,
  projectId,
  panelId,
  onProject,
  onPanel,
}: {
  projects: Project[];
  panels: Panel[];
  projectId: string;
  panelId: string;
  onProject: (value: string) => void;
  onPanel: (value: string) => void;
}) {
  const scoped = panels.filter((panel) => panel.projectId === projectId);
  return (
    <div className="scope-row">
      <Field label="프로젝트">
        <select
          value={projectId}
          onChange={(event) => {
            onProject(event.target.value);
            onPanel("");
          }}
        >
          <option value="">선택</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.title}
            </option>
          ))}
        </select>
      </Field>
      <Field label="AI 패널">
        <select
          value={panelId}
          onChange={(event) => onPanel(event.target.value)}
          disabled={!projectId}
        >
          <option value="">선택</option>
          {scoped.map((panel) => (
            <option key={panel.id} value={panel.id}>
              {panel.title}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

function Workspace() {
  const { me, logout } = useAuth();
  const [section, setSection] = useState<Section>("projects");
  const [projects, setProjects] = useState<Project[]>([]);
  const [panels, setPanels] = useState<Panel[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const [nextProjects, nextPanels, nextSurveys] = await Promise.all([
        researchApi.projects(),
        researchApi.panels(),
        researchApi.surveys(),
      ]);
      setProjects(nextProjects);
      setPanels(nextPanels);
      setSurveys(nextSurveys);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "목록을 불러오지 못했습니다.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function action(
    run: () => Promise<void>,
    success?: string,
    label = "처리 중입니다.",
  ) {
    setBusy(true);
    setBusyLabel(label);
    setError("");
    setNotice("");
    try {
      await run();
      if (success) setNotice(success);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "요청을 처리하지 못했습니다.",
      );
    } finally {
      setBusy(false);
      setBusyLabel("");
    }
  }

  const title = nav.find((item) => item.id === section)?.label;
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="logo-mark">AI</div>
          <strong>KitHub AI 검증</strong>
        </div>
        <nav>
          {nav.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={section === id ? "active" : ""}
              onClick={() => setSection(id)}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <span>v{__APP_VERSION__}</span>
          <button onClick={() => void logout()}>
            <LogOut size={16} /> 로그아웃
          </button>
        </div>
      </aside>
      <div className="main-area">
        <header>
          <div>
            <p>{me?.user?.phoneNumber || "공급자 계정"}</p>
            <h1>{title}</h1>
          </div>
        </header>
        {busy ? (
          <div className="processing-banner">
            <span className="processing-shimmer" />
            <LoaderCircle className="spin" size={17} />
            {busyLabel}
          </div>
        ) : null}
        <main>
          {error ? <div className="alert error-box">{error}</div> : null}
          {notice ? <div className="alert success-box">{notice}</div> : null}
          {loading ? (
            <div className="loading">
              <LoaderCircle className="spin" /> 목록 불러오는 중
            </div>
          ) : null}
          {!loading && section === "projects" ? (
            <ProjectsSection
              projects={projects}
              busy={busy}
              action={action}
              reload={reload}
            />
          ) : null}
          {!loading && section === "panels" ? (
            <PanelsSection
              projects={projects}
              panels={panels}
              busy={busy}
              action={action}
              reload={reload}
            />
          ) : null}
          {!loading && section === "surveys" ? (
            <SurveysSection
              projects={projects}
              panels={panels}
              surveys={surveys}
              busy={busy}
              action={action}
              reload={reload}
            />
          ) : null}
          {!loading && section === "message-test" ? (
            <MessageTestSection
              projects={projects}
              panels={panels}
              busy={busy}
              action={action}
            />
          ) : null}
          {!loading && section === "conversation" ? (
            <ConversationSection
              projects={projects}
              panels={panels}
              busy={busy}
              action={action}
            />
          ) : null}
          {!loading && section === "survey-test" ? (
            <SurveyTestSection
              surveys={surveys}
              busy={busy}
              action={action}
              reload={reload}
            />
          ) : null}
        </main>
      </div>
    </div>
  );
}

function ProjectsSection({
  projects,
  busy,
  action,
  reload,
}: {
  projects: Project[];
  busy: boolean;
  action: Action;
  reload: () => Promise<void>;
}) {
  const [draft, setDraft] = useState({
    title: "",
    productDescription: "",
    hypothesis: "",
    validationGoal: "",
  });
  const canSuggest = Boolean(
    draft.title.trim() && draft.productDescription.trim(),
  );

  async function suggest() {
    await action(
      async () => setDraft(await researchApi.suggestProject(draft)),
      "검증 가설을 생성했습니다.",
      "제품 정보를 바탕으로 가설을 생성 중입니다.",
    );
  }

  async function create() {
    await action(
      async () => {
        await researchApi.createProject(draft);
        setDraft({
          title: "",
          productDescription: "",
          hypothesis: "",
          validationGoal: "",
        });
        await reload();
      },
      "프로젝트를 생성했습니다.",
      "프로젝트를 저장 중입니다.",
    );
  }

  return (
    <div className="section-grid">
      <section className="panel sticky-form">
        <div className="panel-title">
          <h2>프로젝트 생성</h2>
          <Button
            secondary
            onClick={suggest}
            disabled={!canSuggest}
            loading={busy}
          >
            <Sparkles size={16} /> AI 가설 생성
          </Button>
        </div>
        <div className="form-stack">
          <Field label="제품명">
            <input
              value={draft.title}
              onChange={(event) =>
                setDraft({ ...draft, title: event.target.value })
              }
            />
          </Field>
          <Field label="제품 설명">
            <textarea
              value={draft.productDescription}
              onChange={(event) =>
                setDraft({ ...draft, productDescription: event.target.value })
              }
            />
          </Field>
          {!canSuggest ? (
            <p className="field-help">
              제품명과 제품 설명을 입력하면 AI 가설 생성이 활성화됩니다.
            </p>
          ) : null}
          <Field label="검증 가설">
            <textarea
              value={draft.hypothesis}
              onChange={(event) =>
                setDraft({ ...draft, hypothesis: event.target.value })
              }
            />
          </Field>
          <Field label="검증 목표">
            <textarea
              value={draft.validationGoal}
              onChange={(event) =>
                setDraft({ ...draft, validationGoal: event.target.value })
              }
            />
          </Field>
          <Button
            onClick={create}
            loading={busy}
            disabled={
              !draft.title || !draft.productDescription || !draft.hypothesis
            }
          >
            <Plus size={16} /> 생성
          </Button>
        </div>
      </section>
      <section className="panel">
        <div className="panel-title">
          <h2>프로젝트 목록</h2>
          <span>{projects.length}</span>
        </div>
        <div className="list">
          {projects.map((project) => (
            <article key={project.id}>
              <div>
                <h3>{project.title}</h3>
                <p>{project.productDescription}</p>
                <strong>{project.hypothesis}</strong>
              </div>
              <span className="status">
                {project.status === "active" ? "진행" : "보관"}
              </span>
            </article>
          ))}
          {!projects.length ? <Empty text="프로젝트가 없습니다." /> : null}
        </div>
      </section>
    </div>
  );
}

function PanelsSection({
  projects,
  panels,
  busy,
  action,
  reload,
}: {
  projects: Project[];
  panels: Panel[];
  busy: boolean;
  action: Action;
  reload: () => Promise<void>;
}) {
  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState<Panel | null>(null);
  const [members, setMembers] = useState<PanelMember[]>([]);
  const [page, setPage] = useState(0);
  const total = selected?.memberCount ?? 0;
  const pageSize = 50;

  async function create() {
    await action(
      async () => {
        await researchApi.createPanel({ projectId, title });
        setTitle("");
        await reload();
      },
      "1,000명 AI 패널을 생성했습니다.",
      "프로젝트 기준으로 1,000명 패널을 생성 중입니다.",
    );
  }

  async function loadPage(panel: Panel, nextPage: number) {
    await action(async () => {
      const detail = await researchApi.panel(
        panel.id,
        nextPage * pageSize,
        pageSize,
      );
      setSelected(detail.panel);
      setMembers(detail.panel.members ?? []);
      setPage(nextPage);
    }, undefined, "패널 구성원을 불러오는 중입니다.");
  }

  return (
    <div className="section-grid">
      <section className="panel sticky-form">
        <h2>AI 패널 생성</h2>
        {!projects.length ? (
          <Empty text="프로젝트를 먼저 생성해 주세요." />
        ) : (
          <div className="form-stack">
            <Field label="프로젝트">
              <select
                value={projectId}
                onChange={(event) => setProjectId(event.target.value)}
              >
                <option value="">선택</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="패널 제목">
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="비워두면 AI가 작성"
              />
            </Field>
            <Button
              onClick={create}
              loading={busy}
              disabled={!projectId}
            >
              <Sparkles size={16} /> 1,000명 생성
            </Button>
          </div>
        )}
      </section>
      <section className="panel">
        <div className="panel-title">
          <h2>AI 패널 목록</h2>
          <span>{panels.length}</span>
        </div>
        <div className="list compact">
          {panels.map((panel) => (
            <button
              className="list-button"
              key={panel.id}
              onClick={() => void loadPage(panel, 0)}
            >
              <div>
                <h3>{panel.title}</h3>
                <p>
                  {panel.projectTitle} · {panel.memberCount.toLocaleString()}명 ·{" "}
                  {panel.summary}
                </p>
              </div>
              <ChevronRight size={18} />
            </button>
          ))}
          {!panels.length ? <Empty text="생성된 패널이 없습니다." /> : null}
        </div>
        {selected ? (
          <div className="member-block">
            <div className="member-head">
              <div>
                <h3>{selected.title}</h3>
                <p>
                  {page * pageSize + 1}–
                  {Math.min((page + 1) * pageSize, total)} /{" "}
                  {total.toLocaleString()}명
                </p>
              </div>
              <div className="pager">
                <button
                  onClick={() => void loadPage(selected, page - 1)}
                  disabled={busy || page === 0}
                  aria-label="이전 패널 페이지"
                >
                  <ChevronLeft size={17} />
                </button>
                <span>
                  {page + 1} / {Math.ceil(total / pageSize)}
                </span>
                <button
                  onClick={() => void loadPage(selected, page + 1)}
                  disabled={busy || (page + 1) * pageSize >= total}
                  aria-label="다음 패널 페이지"
                >
                  <ChevronRight size={17} />
                </button>
              </div>
            </div>
            <div className="member-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상태</th>
                    <th>연령</th>
                    <th>성별</th>
                    <th>직업</th>
                    <th>가구</th>
                    <th>관심 제품</th>
                    <th>구매 기준</th>
                    <th>구매 성향</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.id}>
                      <td>{member.id}</td>
                      <td>
                        <span className="status">준비</span>
                      </td>
                      <td>{member.ageRange}</td>
                      <td>{member.gender}</td>
                      <td>{member.occupationType}</td>
                      <td>{member.householdType}</td>
                      <td>{member.productCategories.join(", ")}</td>
                      <td>{member.selectionPriority}</td>
                      <td>{member.purchaseTendency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function SurveysSection({
  projects,
  panels,
  surveys,
  busy,
  action,
  reload,
}: {
  projects: Project[];
  panels: Panel[];
  surveys: Survey[];
  busy: boolean;
  action: Action;
  reload: () => Promise<void>;
}) {
  const [projectId, setProjectId] = useState("");
  const [panelId, setPanelId] = useState("");
  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState<Survey | null>(null);

  async function create() {
    await action(
      async () => {
        const survey = await researchApi.createSurvey({
          projectId,
          panelId,
          title,
        });
        setSelected(survey);
        await reload();
      },
      "AI 설문을 생성했습니다.",
      "가설과 패널을 분석해 10~15개 문항을 생성 중입니다.",
    );
  }

  function updateQuestion(index: number, patch: Partial<SurveyQuestion>) {
    if (!selected) return;
    setSelected({
      ...selected,
      questions: selected.questions.map((question, questionIndex) =>
        questionIndex === index ? { ...question, ...patch } : question,
      ),
    });
  }

  async function saveSurvey() {
    if (!selected) return;
    await action(
      async () => {
        const saved = await researchApi.updateSurvey(selected.id, {
          title: selected.title,
          questions: selected.questions,
        });
        setSelected(saved);
        await reload();
      },
      "설문을 저장했습니다.",
      "설문 변경사항을 저장 중입니다.",
    );
  }

  return (
    <div className="section-grid survey-layout">
      <section className="panel sticky-form">
        <h2>설문 생성</h2>
        <div className="form-stack">
          <SelectScope
            projects={projects}
            panels={panels}
            projectId={projectId}
            panelId={panelId}
            onProject={setProjectId}
            onPanel={setPanelId}
          />
          <Field label="설문 제목">
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="비워두면 AI가 작성"
            />
          </Field>
          <Button
            onClick={create}
            loading={busy}
            disabled={!projectId || !panelId}
          >
            <Sparkles size={16} /> 10~15문항 생성
          </Button>
        </div>
        <div className="list survey-picker">
          {surveys.map((survey) => (
            <button
              className="list-button"
              key={survey.id}
              onClick={() =>
                setSelected({
                  ...survey,
                  questions: survey.questions.map((question) => ({
                    ...question,
                    options: [...question.options],
                  })),
                })
              }
            >
              <div>
                <h3>{survey.title}</h3>
                <p>
                  {survey.projectTitle} · {survey.panelTitle} ·{" "}
                  {survey.questions.length}문항
                </p>
              </div>
              <ChevronRight size={18} />
            </button>
          ))}
        </div>
      </section>
      <section className="panel survey-editor">
        <div className="panel-title">
          <h2>설문 문항</h2>
          {selected ? (
            <Button onClick={saveSurvey} loading={busy}>
              <Save size={16} /> 저장
            </Button>
          ) : null}
        </div>
        {!selected ? (
          <Empty text="설문을 생성하거나 목록에서 선택해 주세요." />
        ) : (
          <div className="form-stack">
            <Field label="설문 제목">
              <input
                value={selected.title}
                onChange={(event) =>
                  setSelected({ ...selected, title: event.target.value })
                }
              />
            </Field>
            <div className="question-editor-list">
              {selected.questions.map((question, index) => (
                <article className="question-editor" key={question.id}>
                  <div className="question-editor-head">
                    <strong>{index + 1}</strong>
                    <select
                      value={question.type}
                      onChange={(event) =>
                        updateQuestion(index, { type: event.target.value })
                      }
                    >
                      <option value="single_choice">단일 선택</option>
                      <option value="multi_choice">복수 선택</option>
                      <option value="scale">척도</option>
                      <option value="short_text">주관식</option>
                    </select>
                  </div>
                  <textarea
                    value={question.text}
                    onChange={(event) =>
                      updateQuestion(index, { text: event.target.value })
                    }
                  />
                  {question.type !== "short_text" ? (
                    <div className="option-editor">
                      {question.options.map((option, optionIndex) => (
                        <div key={`${question.id}-${optionIndex}`}>
                          <span>{optionIndex + 1}</span>
                          <input
                            value={option}
                            onChange={(event) =>
                              updateQuestion(index, {
                                options: question.options.map(
                                  (item, itemIndex) =>
                                    itemIndex === optionIndex
                                      ? event.target.value
                                      : item,
                                ),
                              })
                            }
                          />
                        </div>
                      ))}
                      <button
                        className="inline-add"
                        onClick={() =>
                          updateQuestion(index, {
                            options: [...question.options, "새 선택지"],
                          })
                        }
                        type="button"
                      >
                        <Plus size={14} /> 선택지 추가
                      </button>
                    </div>
                  ) : (
                    <div className="short-answer-preview">주관식 답변</div>
                  )}
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function EvidenceList({
  title,
  values,
}: {
  title: string;
  values: unknown;
}) {
  const rows = Array.isArray(values) ? (values as Array<JsonObject>) : [];
  return (
    <div>
      <b>{title}</b>
      <span>
        {rows
          .map((row) => `${String(row.label)} ${Number(row.count)}명`)
          .join(" · ") || "근거 없음"}
      </span>
    </div>
  );
}

function MessageTestSection({
  projects,
  panels,
  busy,
  action,
}: {
  projects: Project[];
  panels: Panel[];
  busy: boolean;
  action: Action;
}) {
  const [projectId, setProjectId] = useState("");
  const [panelId, setPanelId] = useState("");
  const [variants, setVariants] = useState(["", ""]);
  const [result, setResult] = useState<JsonObject | null>(null);

  async function run() {
    await action(
      async () =>
        setResult(
          (
            await researchApi.runMessageTest({
              projectId,
              panelId,
              variants,
            })
          ).result,
        ),
      "투표를 완료했습니다.",
      "1,000명의 문구 선택과 세그먼트 근거를 계산 중입니다.",
    );
  }

  const votes = Array.isArray(result?.votes)
    ? (result.votes as Array<JsonObject>)
    : [];
  return (
    <section className="panel wide-panel">
      <h2>문구 테스트</h2>
      <SelectScope
        projects={projects}
        panels={panels}
        projectId={projectId}
        panelId={panelId}
        onProject={setProjectId}
        onPanel={setPanelId}
      />
      <div className="variants">
        {variants.map((value, index) => (
          <Field key={index} label={`문구 ${String.fromCharCode(65 + index)}`}>
            <textarea
              value={value}
              onChange={(event) =>
                setVariants(
                  variants.map((item, itemIndex) =>
                    itemIndex === index ? event.target.value : item,
                  ),
                )
              }
            />
          </Field>
        ))}
      </div>
      <div className="actions">
        <Button
          secondary
          onClick={() => setVariants([...variants, ""])}
          disabled={variants.length >= 8}
        >
          <Plus size={16} /> 문구 추가
        </Button>
        <Button
          onClick={run}
          loading={busy}
          disabled={
            !projectId || !panelId || variants.filter(Boolean).length < 2
          }
        >
          1,000명 투표
        </Button>
      </div>
      {result ? (
        <div className="result-card">
          <h3>{String(result.summary ?? "")}</h3>
          {votes.map((vote) => {
            const evidence =
              vote.evidence && typeof vote.evidence === "object"
                ? (vote.evidence as JsonObject)
                : {};
            const samples = Array.isArray(evidence.sampleVoters)
              ? (evidence.sampleVoters as Array<JsonObject>)
              : [];
            return (
              <article className="vote-detail" key={String(vote.id)}>
                <div className="vote">
                  <span>
                    {String(vote.id)} · {String(vote.text)}
                  </span>
                  <strong>
                    {Number(vote.count).toLocaleString()}표 ({String(vote.percent)}%)
                  </strong>
                </div>
                <div className="evidence-grid">
                  <EvidenceList title="연령" values={evidence.ageRanges} />
                  <EvidenceList title="성별" values={evidence.genders} />
                  <EvidenceList
                    title="구매 기준"
                    values={evidence.selectionPriorities}
                  />
                  <EvidenceList
                    title="구매 성향"
                    values={evidence.purchaseTendencies}
                  />
                </div>
                <div className="sample-voters">
                  {samples.map((sample) => (
                    <span key={String(sample.id)}>
                      {String(sample.id)} · {String(sample.ageRange)} ·{" "}
                      {String(sample.gender)} · {String(sample.occupationType)} ·{" "}
                      {String(sample.selectionPriority)}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function PanelAnswerEvidence({ meta }: { meta?: JsonObject }) {
  if (
    !meta ||
    Number(meta.respondentCount ?? 0) < 2 ||
    !meta.stanceCounts ||
    typeof meta.stanceCounts !== "object"
  )
    return null;
  const counts =
    meta.stanceCounts && typeof meta.stanceCounts === "object"
      ? (meta.stanceCounts as JsonObject)
      : {};
  const priorities = Array.isArray(meta.topPriorities)
    ? (meta.topPriorities as Array<JsonObject>)
    : [];
  return (
    <div className="panel-answer-evidence">
      <div>
        <span>긍정 {Number(counts.positive ?? 0).toLocaleString()}명</span>
        <span>중립 {Number(counts.neutral ?? 0).toLocaleString()}명</span>
        <span>부정 {Number(counts.negative ?? 0).toLocaleString()}명</span>
      </div>
      <small>
        주요 판단 기준: {priorities
          .map((item) => `${String(item.label)} ${Number(item.count)}명`)
          .join(" · ")}
      </small>
    </div>
  );
}

function ConversationSection({
  projects,
  panels,
  busy,
  action,
}: {
  projects: Project[];
  panels: Panel[];
  busy: boolean;
  action: Action;
}) {
  const [projectId, setProjectId] = useState("");
  const [panelId, setPanelId] = useState("");
  const [memberId, setMemberId] = useState("all");
  const [members, setMembers] = useState<PanelMember[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [input, setInput] = useState("");
  const messagesRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages, busy]);

  async function selectPanel(value: string) {
    setPanelId(value);
    setMemberId("all");
    setConversation(null);
    setMessages([]);
    setMembers([]);
    if (!value) return;
    await action(async () => {
      const detail = await researchApi.panel(value, 0, 1000);
      setMembers(detail.panel.members ?? []);
    }, undefined, "1,000명 패널 구성원을 불러오는 중입니다.");
  }

  async function start() {
    const targetMemberId = memberId === "all" ? null : memberId;
    await action(async () => {
      const existing = await researchApi.conversations(
        projectId,
        panelId,
        targetMemberId,
      );
      const next =
        existing[0] ??
        (await researchApi.createConversation({
          projectId,
          panelId,
          memberId: targetMemberId,
        }));
      setConversation(next);
      setMessages(await researchApi.messages(next.id));
    }, undefined, "대화방을 여는 중입니다.");
  }

  async function send() {
    if (!conversation || !input.trim()) return;
    const content = input.trim();
    setInput("");
    setMessages((current) => [
      ...current,
      {
        id: `temp-${Date.now()}`,
        role: "user",
        content,
        createdAt: new Date().toISOString(),
      },
    ]);
    await action(async () => {
      await researchApi.sendMessage(conversation.id, content);
      setMessages(await researchApi.messages(conversation.id));
    }, undefined, memberId === "all" ? "1,000명 패널 응답을 집계 중입니다." : `${memberId}의 답변을 생성 중입니다.`);
  }

  function onComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!busy && input.trim()) void send();
    }
  }

  return (
    <section className="panel wide-panel chat-panel">
      <h2>패널 대화</h2>
      <SelectScope
        projects={projects}
        panels={panels}
        projectId={projectId}
        panelId={panelId}
        onProject={(value) => {
          setProjectId(value);
          setPanelId("");
          setMembers([]);
          setConversation(null);
          setMessages([]);
        }}
        onPanel={(value) => void selectPanel(value)}
      />
      <div className="conversation-target">
        <Field label="대화 대상">
          <select
            value={memberId}
            onChange={(event) => {
              setMemberId(event.target.value);
              setConversation(null);
              setMessages([]);
            }}
            disabled={!panelId || !members.length}
          >
            <option value="all">전체 패널 1,000명</option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.id} · {member.ageRange} · {member.gender} ·{" "}
                {member.occupationType} · {member.selectionPriority}
              </option>
            ))}
          </select>
        </Field>
        <Button
          onClick={start}
          loading={busy}
          disabled={!projectId || !panelId}
        >
          대화 열기
        </Button>
      </div>
      {conversation ? (
        <div className="chat-room">
          <div className="chat-room-head">
            <strong>{conversation.title}</strong>
            <span>
              {memberId === "all" ? "1,000명 집계" : `${memberId} 개인 대화`}
            </span>
          </div>
          <div className="messages" ref={messagesRef}>
            {messages.map((message) => (
              <article key={message.id} className={message.role}>
                <b>{message.role === "user" ? "질문" : "AI 패널"}</b>
                <p>{message.content}</p>
                {message.role === "assistant" ? (
                  <PanelAnswerEvidence meta={message.meta} />
                ) : null}
                {message.role === "assistant" ? (
                  <small>
                    {memberId === "all"
                      ? "합성 패널 1,000명 시뮬레이션"
                      : `${memberId} 합성 소비자 시뮬레이션`}
                  </small>
                ) : null}
              </article>
            ))}
            {!messages.length ? (
              <Empty text="가격, 문구, 구매 이유 등 무엇이든 질문하세요." />
            ) : null}
            {busy ? <div className="message-skeleton" /> : null}
          </div>
          <div className="composer">
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={onComposerKeyDown}
              placeholder="Enter 전송 · Shift+Enter 줄바꿈"
            />
            <Button onClick={send} loading={busy} disabled={!input.trim()}>
              <Send size={16} /> 전송
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ResultDetail({ result }: { result: JsonObject }) {
  const aggregates = Array.isArray(result.aggregates)
    ? (result.aggregates as Array<JsonObject>)
    : [];
  const findings = Array.isArray(result.findings)
    ? (result.findings as unknown[])
    : [];
  return (
    <div className="result-card survey-result-detail">
      <h3>{String(result.summary ?? "")}</h3>
      {findings.length ? (
        <ul>
          {findings.map((finding, index) => (
            <li key={index}>{String(finding)}</li>
          ))}
        </ul>
      ) : null}
      <p>{String(result.recommendation ?? "")}</p>
      <div className="aggregate-list">
        {aggregates.map((item, index) => {
          const options = Array.isArray(item.options)
            ? (item.options as Array<JsonObject>)
            : [];
          const themes = Array.isArray(item.themes)
            ? (item.themes as unknown[])
            : [];
          return (
            <article key={String(item.id ?? index)}>
              <div className="aggregate-head">
                <b>
                  {index + 1}. {String(item.text ?? "")}
                </b>
                <span>{Number(item.responseCount ?? 0).toLocaleString()}명</span>
              </div>
              {options.length ? (
                <div className="option-results">
                  {options.map((option, optionIndex) => (
                    <div key={optionIndex}>
                      <span>{String(option.label)}</span>
                      <i>
                        <em
                          style={{
                            width: `${Math.min(100, Number(option.percent ?? 0))}%`,
                          }}
                        />
                      </i>
                      <strong>
                        {Number(option.count).toLocaleString()}명 ·{" "}
                        {String(option.percent)}%
                      </strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="theme-results">
                  {themes.map((theme, themeIndex) => (
                    <span key={themeIndex}>{String(theme)}</span>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function SurveyTestSection({
  surveys,
  busy,
  action,
  reload,
}: {
  surveys: Survey[];
  busy: boolean;
  action: Action;
  reload: () => Promise<void>;
}) {
  const [surveyId, setSurveyId] = useState("");
  const [runs, setRuns] = useState<TestRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<TestRun | null>(null);
  const survey = surveys.find((item) => item.id === surveyId);

  async function loadRuns(preferredRunId?: string) {
    const next = await researchApi.testRuns("survey");
    setRuns(next);
    setSelectedRun(
      next.find((run) => run.id === preferredRunId) ?? next[0] ?? null,
    );
  }

  useEffect(() => {
    void loadRuns();
  }, []);

  async function run() {
    await action(
      async () => {
        const response = await researchApi.runSurvey(surveyId);
        await Promise.all([reload(), loadRuns(response.runId)]);
      },
      "설문 시뮬레이션을 완료했습니다.",
      "1,000명의 설문 응답과 문항별 결과를 생성 중입니다.",
    );
  }

  return (
    <div className="survey-test-layout">
      <section className="panel survey-test-controls">
        <h2>설문 테스트</h2>
        <Field label="설문">
          <select
            value={surveyId}
            onChange={(event) => setSurveyId(event.target.value)}
          >
            <option value="">선택</option>
            {surveys.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title} · {item.projectTitle} · {item.panelTitle}
              </option>
            ))}
          </select>
        </Field>
        {survey ? (
          <div className="selected-info">
            <strong>{survey.projectTitle}</strong>
            <span>{survey.panelTitle}</span>
            <span>{survey.questions.length}문항</span>
          </div>
        ) : null}
        <Button onClick={run} loading={busy} disabled={!surveyId}>
          AI 설문 실행
        </Button>
        <div className="run-list">
          <h3>결과 목록</h3>
          {runs.map((item) => (
            <button
              key={item.id}
              className={selectedRun?.id === item.id ? "selected" : ""}
              onClick={() => setSelectedRun(item)}
            >
              <strong>{item.surveyTitle}</strong>
              <span>
                {item.projectTitle} · {item.panelTitle}
              </span>
              <small>{new Date(item.createdAt).toLocaleString("ko-KR")}</small>
            </button>
          ))}
          {!runs.length ? <Empty text="실행된 설문 결과가 없습니다." /> : null}
        </div>
      </section>
      <section className="panel survey-test-result">
        <div className="panel-title">
          <h2>문항별 결과</h2>
          {selectedRun ? <span>1,000명</span> : null}
        </div>
        {selectedRun ? (
          <ResultDetail result={selectedRun.result} />
        ) : (
          <Empty text="결과 목록에서 실행 결과를 선택해 주세요." />
        )}
      </section>
    </div>
  );
}

function AuthGate() {
  const { stage, logout } = useAuth();
  if (stage === "booting") {
    return (
      <div className="loading full">
        <LoaderCircle className="spin" /> 확인 중
      </div>
    );
  }
  if (stage === "anonymous") return <LoginPage />;
  if (stage === "pending") {
    return (
      <main className="login-page">
        <section className="login-card">
          <div className="logo-mark">AI</div>
          <h1>승인된 공급자 계정이 필요합니다.</h1>
          <Button onClick={() => void logout()}>로그아웃</Button>
        </section>
      </main>
    );
  }
  return <Workspace />;
}

export function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}
